import React, { useState } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  Button,
  Card,
  Text,
  Flex,
  Box,
  Notice,
  Accordion,
  HelpText,
  ProgressIndicator,
  Tag,
  Tooltip,
  SrOnly,
  Stepper,
} from '@applyboard/crystal-ui';
import {
  ArrowLeftOutlineIcon,
  ArrowRightOutlineIcon,
  DocumentCheckOutlineIcon,
  InfoOutlineIcon,
  WarningOutlineIcon,
} from '@applyboard/ui-icons';
import { NetworkError } from '@applyboard/ui-utils';
import {
  Context,
  Events,
  getWizardSteps,
  isInStep,
  isPrerequisiteStepRequirement,
  shouldShowEligibilityStep,
  shouldShowIntakeStepBasedOnState,
  State,
  useApplicationWizardStateMachine,
} from './machine';
import { EligibilityStep } from './EligibilityStep';
import { IntakeStep } from './IntakeStep';
import { ExpectedRequirement, Program, School, SelectedIntake, WarningInsight } from './data';
import { convertTimelessDateStrToLocalDate, formatDateWithTimezone } from '~/utils/dates';
import { Student } from '~/data/eligibility';
import { WarningInsightsDialog } from './WarningInsightDialog';
import { RawHtml } from '../RawHtml';

type CreateApplicationButtonPageName =
  | 'Program'
  | 'School'
  | 'Quick Search'
  | 'Applications Cart'
  | 'Wizard Demo'
  | 'Search'
  | 'Application Page';

/**
 * See full documentation here:
 * https://applyboard.atlassian.net/wiki/spaces/APP/pages/2903638553/CreateApplicationButton
 */
export interface CreateApplicationButtonProps {
  program: Program;
  currentProgram?: Partial<Program>;
  student?: Student;
  school: School;
  currentSchool?: Partial<School>;
  unknownStudentEligibility?: boolean;
  disableStudentSelector?: boolean;
  label?: string;
  pageName: CreateApplicationButtonPageName;
  onClick?: () => void;
  selectedIntake?: SelectedIntake;
  buttonEmphasis?: 'contained' | 'outlined';
  buttonWidth?: 'hug' | 'fill';
  hideIcon?: boolean;
}

/**
 * See full documentation here:
 * https://applyboard.atlassian.net/wiki/spaces/APP/pages/2903638553/CreateApplicationButton
 */
export function CreateApplicationButton({
  label = 'Create application',
  program,
  currentProgram,
  school,
  currentSchool,
  student,
  unknownStudentEligibility,
  disableStudentSelector,
  pageName,
  onClick,
  selectedIntake: preSelectedIntake,
  buttonEmphasis = 'outlined',
  buttonWidth = 'hug',
  hideIcon,
}: CreateApplicationButtonProps) {
  const [state, dispatch] = useApplicationWizardStateMachine();
  const [studentSearchError, setStudentSearchError] = useState<string | null>(null);

  return (
    <Dialog
      open={state.value !== 'closed_dialog'}
      onOpenChange={open => {
        if (open) {
          onClick?.();
          dispatch({
            type: 'OPENED_DIALOG',
            student,
            program,
            currentProgram,
            school,
            currentSchool,
            preSelectedIntake,
            unknownStudentEligibility,
            pageName,
          });
        } else {
          dispatch({ type: 'CLOSED_DIALOG' });
          setStudentSearchError(null);
        }
      }}
      size="lg"
    >
      <Dialog.Trigger>
        <Button
          intent="primary"
          emphasis={buttonEmphasis}
          trailIcon={hideIcon ? undefined : DocumentCheckOutlineIcon}
          width={buttonWidth}
        >
          {label}
        </Button>
      </Dialog.Trigger>
      <Dialog.Heading>New Application</Dialog.Heading>
      <Dialog.Content>
        {/* Temporary, so we can see the state when flaky tests fail */}
        {process.env.NODE_ENV === 'test' && (
          <div style={{ display: 'none' }}>{JSON.stringify(state)}</div>
        )}
        <div role="status" aria-live="polite">
          {studentSearchError && (
            <Notice intent="negative">
              Your request cannot be completed due to {studentSearchError}. Please try again in a
              few minutes. If the error persists feel free to contact our support team.
            </Notice>
          )}
        </div>
        <Box pb={6}>
          <ApplicationWizardStepper state={state} />
        </Box>
        <Card emphasis="outlined" aria-label="Application Info">
          <KeyValueList>
            <>
              <KeyValueList.Key>Program</KeyValueList.Key>
              <KeyValueList.Value>{state.context.selectedProgram?.name}</KeyValueList.Value>
              {state.context.showProgramWarnings && (
                <SelectedProgramInfo
                  programWarnings={state.context.programWarnings}
                  dispatch={dispatch}
                />
              )}
            </>
            {state.context.selectedStudent && (
              <>
                <KeyValueList.Key>Student</KeyValueList.Key>
                <KeyValueList.Value>{state.context.selectedStudent.name}</KeyValueList.Value>
              </>
            )}
            {state.context.selectedIntake?.intakeId && (
              <SelectedIntakeInfo
                selectedIntake={state.context.selectedIntake || preSelectedIntake}
                dispatch={dispatch}
              />
            )}
          </KeyValueList>
        </Card>
        {isInStep('eligibilityStep', state) && (
          <EligibilityStep
            state={state}
            dispatch={dispatch}
            disableStudentSelector={disableStudentSelector}
            onSearchError={err =>
              setStudentSearchError(
                err instanceof NetworkError ? 'a network error' : 'an error on our end',
              )
            }
            onSearchSuccess={() => setStudentSearchError(null)}
          />
        )}
        {isInStep('intakeStep', state) && <IntakeStep state={state} dispatch={dispatch} />}
        {isInStep('prerequisitesStep', state) && (
          <PrerequisitesStep state={state} dispatch={dispatch} />
        )}
        {isInStep('whatToExpectStep', state) && (
          <WhatToExpectStep state={state} dispatch={dispatch} />
        )}
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton>Cancel</Dialog.CloseButton>
        <Flex gap={2}>
          {getActiveStep(state) !== 0 && (
            <Button
              intent="primary"
              emphasis="outlined"
              leadIcon={ArrowLeftOutlineIcon}
              onClick={() => {
                dispatch({ type: 'PREV_BUTTON_CLICKED' });
              }}
              disabled={state.value === 'creating_application'}
            >
              Previous
            </Button>
          )}
          {isInStep('whatToExpectStep', state) ? (
            <Button
              intent="primary"
              onClick={() => {
                dispatch({ type: 'NEXT_BUTTON_CLICKED' });

                const selectedIntake = state.context.selectedIntake;

                // TODO: move this to href prop after https://applyboard.atlassian.net/browse/UIE-882
                window.location.href = `/apply_program?program_id=${
                  state.context.selectedProgram?.id
                }&student_id=${state.context.selectedStudent?.id}${
                  selectedIntake?.intakeId ? `&intake_id=${selectedIntake.intakeId}` : ''
                }`;
              }}
              loading={state.value === 'creating_application'}
            >
              Create application
            </Button>
          ) : (
            <Button
              intent="primary"
              trailIcon={ArrowRightOutlineIcon}
              onClick={() => dispatch({ type: 'NEXT_BUTTON_CLICKED' })}
              disabled={
                isInStep('eligibilityStep', state) &&
                state.value !== 'eligible' &&
                !state.context.eligibilityPermissions?.canApplyIgnoringEligibility
              }
            >
              Next
            </Button>
          )}
        </Flex>
      </Dialog.Actions>
    </Dialog>
  );
}

const WarningTitle = ({
  warningInsights,
  context,
  dispatch,
}: {
  warningInsights: WarningInsight[];
  context: 'program' | 'intake';
  dispatch: React.Dispatch<Events>;
}) => {
  const title = context === 'program' ? 'Important Program Details' : 'Important Intake Details';

  return (
    <KeyValueList.Value>
      <Flex gap={2} direction="column">
        <Flex gap={2} align="center">
          <WarningOutlineIcon intent="warning" />
          <Text variant="bodyM" intent="warning">
            {title}{' '}
          </Text>

          <WarningInsightsDialog
            warningInsights={warningInsights ?? []}
            context={context}
            dispatch={dispatch}
          />
        </Flex>
      </Flex>
    </KeyValueList.Value>
  );
};

const SelectedIntakeInfo = ({
  selectedIntake,
  dispatch,
}: {
  selectedIntake?: SelectedIntake | Record<string, never>;
  dispatch: React.Dispatch<Events>;
}) => {
  if (!selectedIntake || !selectedIntake.startDate) {
    return null;
  }

  const intakeWarnings = selectedIntake?.warningInsights;
  const isIntakeHasWarning = (intakeWarnings?.length ?? 0) > 0;

  return (
    <>
      {selectedIntake && (
        <>
          <KeyValueList.Key>
            <Flex align="center" gap="2px">
              <div
                css={{
                  '@media(max-width: 430px)': {
                    whiteSpace: 'normal',
                  },
                }}
              >
                Intake
              </div>
              <div
                css={{
                  whiteSpace: 'normal',
                  height: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip label="You can select a different intake in the cart once application is created.">
                  <Button
                    aria-label="More info about intakes"
                    emphasis="transparent"
                    size="sm"
                    leadIcon={InfoOutlineIcon}
                  />
                </Tooltip>
              </div>
            </Flex>
          </KeyValueList.Key>
          <KeyValueList.Value>
            {format(convertTimelessDateStrToLocalDate(selectedIntake.startDate), 'LLL yyyy')}
          </KeyValueList.Value>

          {isIntakeHasWarning && (
            <WarningTitle warningInsights={intakeWarnings} context="intake" dispatch={dispatch} />
          )}
          {selectedIntake?.submissionDeadline && (
            <>
              <KeyValueList.Key>Submission deadline</KeyValueList.Key>
              <KeyValueList.Value>
                {formatDateWithTimezone(selectedIntake.submissionDeadline)}
              </KeyValueList.Value>
            </>
          )}
        </>
      )}
    </>
  );
};

const SelectedProgramInfo = ({
  programWarnings,
  dispatch,
}: {
  programWarnings?: WarningInsight[];
  dispatch: React.Dispatch<Events>;
}) => {
  const isProgramHasWarning = (programWarnings?.length ?? 0) > 0;

  if (!isProgramHasWarning) {
    return null;
  }

  return (
    <WarningTitle warningInsights={programWarnings ?? []} context="program" dispatch={dispatch} />
  );
};

function ApplicationWizardStepper({ state }: { state: State }) {
  const activeStep = getActiveStep(state);
  const steps = [
    <Stepper.Step key={3}>
      <Stepper.Step.Label>Prerequisites</Stepper.Step.Label>
    </Stepper.Step>,
    <Stepper.Step key={4}>
      <Stepper.Step.Label>What to Expect</Stepper.Step.Label>
    </Stepper.Step>,
  ];

  if (shouldShowIntakeStepBasedOnState(state)) {
    steps.unshift(
      <Stepper.Step key={2}>
        <Stepper.Step.Label>Intake</Stepper.Step.Label>
      </Stepper.Step>,
    );
  }

  if (shouldShowEligibilityStep(state)) {
    steps.unshift(
      <Stepper.Step key={1}>
        <Stepper.Step.Label>Eligibility</Stepper.Step.Label>
      </Stepper.Step>,
    );
  }
  return (
    <Stepper activeStep={activeStep} aria-label="Create Application Wizard">
      {steps}
    </Stepper>
  );
}

function getActiveStep(state: State) {
  const steps = getWizardSteps(state);

  return steps.findIndex(step => isInStep(step, state));
}

function PrerequisitesStep({
  state,
  dispatch,
}: {
  state: State;
  dispatch: React.Dispatch<Events>;
}) {
  const expectedRequirements = state.context.expectedRequirements.filter(
    isPrerequisiteStepRequirement,
  );

  if (state.value === 'loading_requirements') {
    return (
      <Box py={4}>
        <ProgressIndicator variant="circular" label="Loading..." indeterminate />
      </Box>
    );
  }

  if (state.value === 'load_requirements_network_error') {
    return (
      <Box pt={4}>
        <Notice intent="negative">
          Your request cannot be completed due to a network error. Please try again in a few
          moments.
        </Notice>
      </Box>
    );
  }

  if (state.value === 'load_requirements_unknown_error') {
    return (
      <Box pt={4}>
        <Notice intent="negative">
          Your request cannot be completed due to an error on our end. Please try again in a few
          moments. If the error persists feel free to contact our support team.
        </Notice>
      </Box>
    );
  }

  if (!expectedRequirements.length) {
    return (
      <Box pt={4}>
        <EmptyState />
      </Box>
    );
  }

  return (
    <Box pt={4}>
      <Notice intent="warning">
        Please review the following carefully. If the student does not meet these prerequisites the
        application will be cancelled.
      </Notice>
      <Box pt={4}>
        <ExpectedRequirementsAccordion
          expectedRequirements={expectedRequirements}
          dispatch={dispatch}
          state={state}
        />
      </Box>
    </Box>
  );
}

function WhatToExpectStep({ state, dispatch }: { state: State; dispatch: React.Dispatch<Events> }) {
  const expectedRequirements = state.context.expectedRequirements.filter(
    expectedRequirement => !isPrerequisiteStepRequirement(expectedRequirement),
  );

  if (!expectedRequirements.length) {
    return (
      <Box pt={4}>
        <EmptyState />
      </Box>
    );
  }

  return (
    <Box pt={4}>
      <Text>
        Please carefully review the following. You will need to meet these requirements to
        successfully submit the application.
      </Text>
      <Box pt={4}>
        <ExpectedRequirementsAccordion
          expectedRequirements={expectedRequirements}
          showRequirementType
          dispatch={dispatch}
          state={state}
        />
      </Box>
    </Box>
  );
}

function EmptyState() {
  return (
    <HelpText showIcon intent="secondary">
      There are no requirements for you to review - please proceed.
    </HelpText>
  );
}

function ExpectedRequirementsAccordion({
  expectedRequirements,
  showRequirementType,
  dispatch,
  state,
}: {
  expectedRequirements: ExpectedRequirement[];
  showRequirementType?: boolean;
  dispatch: React.Dispatch<Events>;
  state: State;
}) {
  const allIds = expectedRequirements.map(item => item.id);
  const [value, setValue] = useState<string | string[]>(allIds);

  return (
    <Flex gap={4} direction="column">
      <Flex gap={2} justify="end">
        <Button
          intent="primary"
          emphasis="outlined"
          size="sm"
          onClick={() => {
            dispatch({ type: 'EXPANDED_ACCORDION' });
            setValue(allIds);
          }}
        >
          Expand all
        </Button>
        <Button
          intent="primary"
          emphasis="outlined"
          size="sm"
          onClick={() => {
            dispatch({ type: 'COLLAPSED_ACCORDION' });

            setValue([]);
          }}
        >
          Collapse all
        </Button>
      </Flex>
      <Accordion value={value} onChange={setValue}>
        {expectedRequirements.map((expectedRequirement, idx) => (
          <Accordion.Item
            id={expectedRequirement.id}
            title={
              <>
                <span>{`${expectedRequirements.length === 1 ? '' : `${idx + 1}. `}${
                  expectedRequirement.title
                }`}</span>
                {showRequirementType && (
                  <RequirementTypeTag expectedRequirement={expectedRequirement} />
                )}
              </>
            }
            key={expectedRequirement.id}
          >
            <RawHtml
              dangerouslySetInnerHTML={{ __html: expectedRequirement.description }}
              truncationSettings={{
                maxChars: 215,
              }}
              onToggleTruncation={isExpanded => {
                const { requirementsEverExpanded, requirementsCurrentlyExpanded } =
                  getExpandedRequirementIds({
                    context: state.context,
                    id: expectedRequirement.id,
                    isExpanded,
                  });
                dispatch({
                  type: 'TOGGLED_SEE_MORE',
                  requirementsCurrentlyExpanded,
                  requirementsEverExpanded,
                });
              }}
            />
          </Accordion.Item>
        ))}
      </Accordion>
    </Flex>
  );
}

function getExpandedRequirementIds({
  context,
  id,
  isExpanded,
}: {
  context: Context;
  id: string;
  isExpanded: boolean;
}) {
  const requirementsEverExpanded = isExpanded
    ? [...context.requirementsEverExpanded.filter(requirementId => requirementId !== id), id]
    : context.requirementsEverExpanded;

  const requirementsCurrentlyExpanded = isExpanded
    ? [...context.requirementsCurrentlyExpanded.filter(requirementId => requirementId !== id), id]
    : context.requirementsCurrentlyExpanded.filter(requirementId => requirementId !== id);

  return {
    requirementsEverExpanded,
    requirementsCurrentlyExpanded,
  };
}

function RequirementTypeTag({ expectedRequirement }: { expectedRequirement: ExpectedRequirement }) {
  const labelToTypeMap: Record<string, string | undefined> = {
    document: 'Document',
    questions: 'Question',
    receipt: 'Receipt',
    payment: 'Payment',
    disclaimer: 'Disclaimer',
  };

  const label = labelToTypeMap[expectedRequirement.requirementType];
  if (!label) {
    return null;
  }

  return (
    <div css={{ display: 'inline-block', marginLeft: '8px' }}>
      <div css={{ display: 'inline-block' }} aria-hidden>
        <Tag intent="primary">{label}</Tag>
      </div>
      <SrOnly>({label} type requirement)</SrOnly>
    </div>
  );
}

function KeyValueList(props: { children: React.ReactNode }) {
  return (
    <dl
      css={{
        display: 'grid',
        gridTemplateColumns: 'min-content 1fr',
        rowGap: '8px',
        marginBottom: 0,
      }}
    >
      {props.children}
    </dl>
  );
}

KeyValueList.Key = function KeyValueListKey(props: { children: React.ReactNode }) {
  return (
    <dt css={{ minWidth: '80px', paddingRight: '24px', whiteSpace: 'nowrap', gridColumn: 1 }}>
      <Text variant="labelM">{props.children}</Text>
    </dt>
  );
};

KeyValueList.Value = function KeyValueListValue(props: { children: React.ReactNode }) {
  return (
    <dd css={{ margin: 0, width: '100%', gridColumn: 2 }}>
      <Text variant="bodyM">{props.children}</Text>
    </dd>
  );
};

KeyValueList.Note = function KeyValueListNote(props: { children: React.ReactNode }) {
  return (
    <div
      css={{
        display: 'inline-block',
        gridColumn: 2,
        '@media(max-width: 500px)': {
          gridColumn: '1 / 3',
        },
      }}
    >
      {props.children}
    </div>
  );
};
